import _ from 'lodash';
import moment from 'moment-timezone';
import React, { FC, ReactNode, useCallback, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { virtualize, SlideRenderProps } from 'react-swipeable-views-utils';
import './style.scss';
import CalendarUtils from '../../../../../../../services/CalendarUtils';

interface DayButtonProps {
  date: moment.Moment;
  onClick: () => void;
  isActive: boolean;
  dayRender?: (date: moment.Moment) => ReactNode;
  getClassName?: (date: number) => string | undefined;
  disabledPastDays?: boolean;
  disabledWeekends?: boolean;
}

const DayButton: FC<DayButtonProps> = ({
  date,
  onClick,
  isActive,
  dayRender,
  getClassName,
  disabledPastDays,
  disabledWeekends,
}) => {
  const now = CalendarUtils.convertToTargetTimezone(moment()).startOf('day');
  const isPastDay = disabledPastDays && date.isBefore(now, 'day');
  const isWeekend = disabledWeekends && CalendarUtils.isWeekend(date);

  let classNames = 'day';
  if (isActive) classNames += ' active';
  if (date.isSame(moment.tz(date.tz()!), 'day')) classNames += ' today';
  if (isPastDay || isWeekend) classNames += ' disabled';

  let extraClassName = getClassName ? getClassName(date.unix()) : undefined;
  if (extraClassName) classNames += ' ' + extraClassName;

  let hour = date.hour();
  let minute = date.minute();

  let daysDifference = date.diff(moment().hour(hour).minute(minute).tz(date.tz()!), 'days');

  const handleClick = () => {
    if (!isPastDay) {
      onClick();
    }
  };

  return (
    <div className={classNames} onClick={handleClick} id={`horizontal-day-${daysDifference}`}>
      <span className="weekday">{date.format('ddd')}</span>
      <span className="monthday">{date.format('D')}</span>
      {dayRender && dayRender(date)}
    </div>
  );
};

interface WeekProps {
  firstDay: moment.Moment;
  onDateSelected: (date: number) => void;
  selectedDate: moment.Moment;
  dayRender?: (date: moment.Moment) => ReactNode;
  getClassName?: (date: number) => string | undefined;
  disabledPastDays?: boolean;
  disabledWeekends?: boolean;
}

const Week: FC<WeekProps> = ({
  firstDay,
  onDateSelected,
  selectedDate,
  dayRender,
  getClassName,
  disabledPastDays,
  disabledWeekends,
}) => {
  const buttons = _.range(7).map((idx) => {
    const date = firstDay.clone().add(idx, 'days');
    const onClick = () => onDateSelected(date.unix());
    return (
      <DayButton
        key={date.unix()}
        date={date}
        isActive={selectedDate.isSame(date, 'day')}
        onClick={onClick}
        dayRender={dayRender}
        getClassName={getClassName}
        disabledPastDays={disabledPastDays}
        disabledWeekends={disabledWeekends}
      />
    );
  });

  return <div className="day-wrapper">{buttons}</div>;
};

interface HorizontalCalendarProps {
  startingDate: moment.Moment;
  selectedDate: moment.Moment;
  onDateSelected: (date: number) => void;
  dayRender?: (date: moment.Moment) => ReactNode;
  getClassName?: (date: number) => string | undefined;
  disabledPastDays?: boolean;
  disabledWeekends?: boolean;
}

const VirtualizedSwipeableViews = virtualize(SwipeableViews);

export const HorizontalCalendar: FC<HorizontalCalendarProps> = ({
  startingDate,
  selectedDate,
  onDateSelected,
  dayRender,
  getClassName,
  disabledPastDays,
  disabledWeekends,
}) => {
  const [index, setIndex] = useState(0);

  const renderWeek = useCallback(
    ({ index, key }: SlideRenderProps) => {
      const firstDay = startingDate
        .clone()
        .day('Sunday')
        .add(index * 7, 'days');

      return (
        <Week
          firstDay={firstDay}
          selectedDate={selectedDate}
          onDateSelected={onDateSelected}
          key={key}
          dayRender={dayRender}
          getClassName={getClassName}
          disabledPastDays={disabledPastDays}
          disabledWeekends={disabledWeekends}
        />
      );
    },
    [
      selectedDate,
      onDateSelected,
      startingDate,
      dayRender,
      getClassName,
      disabledPastDays,
      disabledWeekends,
    ],
  );

  const month = startingDate
    .clone()
    .day('Sunday')
    .add(index * 7, 'days')
    .format('MMMM');

  return (
    <div className="horizontal-calendar">
      <span className="month">{month}</span>
      <div className="calendar-with-buttons">
        <div
          className="left-button date-window-button"
          onClick={() => setIndex((prev) => prev - 1)}
        >
          <span>&lt;</span>
        </div>
        <VirtualizedSwipeableViews
          className="virtual-calendar"
          index={index}
          onChangeIndex={setIndex}
          slideRenderer={renderWeek}
        />
        <div
          className="right-button date-window-button"
          onClick={() => setIndex((prev) => prev + 1)}
        >
          <span>&gt;</span>
        </div>
      </div>
    </div>
  );
};
